/**
 * SanctaHeader — chat window titlebar.
 *
 * Reads all its state from `SanctaChatContext`. Wrapped in `React.memo`
 * so it does NOT re-render when the parent's `messages` array grows one
 * character at a time during SSE streaming — a huge perf win because
 * the header contains a 12-item language menu.
 *
 * Extracted iter-131 · rewired to context iter-133.
 */
import React from "react";
import { Globe, Check, Volume2, Maximize2, BrainCircuit, X } from "lucide-react";
import { SANCTA_LANGUAGES } from "@/hooks/useSanctaLocale";
import { useSanctaChat } from "@/contexts/SanctaChatContext";

/**
 * Internal implementation. **Do NOT add props to this component** — it is
 * intentionally 0-prop so that `React.memo` on the exported wrapper
 * shallow-compares an empty props object and re-renders only when the
 * context value's identity changes (guarded by useMemo in the provider).
 * Adding a prop here silently defeats the whole point of the refactor
 * (streaming tokens would re-render the header again).
 */
const SanctaHeaderImpl: React.FC = () => {
  const {
    variant, isMobile, widgetDragProps, chatbotName, chatbotAccent, tagline,
    activeLabel, ttsSpeaking, ttsSupported, langCode, setLangCode, langNativeName,
    langPickerOpen, setLangPickerOpen, detection, autoSwap, setAutoSwap,
    autoRead, setAutoRead, voiceReplyOn, voiceReplyOff, voiceReplyHint,
    showUserActions, onExpand, onMemoryOpen, onClose,
  } = useSanctaChat();

  const headerCls =
    `px-4 py-3 border-b flex items-center gap-3 ${
      variant === "page"
        ? "border-neutral-200 bg-neutral-50 dark:border-white/10 dark:bg-slate-950/40"
        : "border-neutral-200 bg-neutral-50"
    } ${variant === "widget" && !isMobile ? "cursor-grab active:cursor-grabbing" : ""}`;

  return (
    <header
      className={headerCls}
      {...(variant === "widget" && !isMobile && widgetDragProps ? widgetDragProps : {})}
      data-testid="sancta-drag-handle"
    >
      <div
        className="h-9 w-9 relative rounded-full bg-gradient-to-br from-orange-500 to-fuchsia-600 grid place-items-center text-white font-semibold text-sm"
        style={{ backgroundImage: `linear-gradient(135deg, ${chatbotAccent}, #d946ef)` }}
      >
        {chatbotName.charAt(0)}
        {ttsSpeaking && (
          <>
            <span aria-hidden className="absolute inset-0 rounded-full border-2 border-fuchsia-400/70 animate-ping" />
            <span
              aria-hidden
              className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-fuchsia-500 border-2 border-white grid place-items-center"
              data-testid="sancta-speaking-pulse"
            >
              <span className="h-1 w-1 rounded-full bg-white animate-pulse" />
            </span>
          </>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-semibold text-sm" data-testid="sancta-header-name">{chatbotName}</span>
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
        </div>
        <div className={`text-[11px] ${variant === "page" ? "text-neutral-500 dark:text-slate-400" : "text-neutral-500"}`}>
          {activeLabel} · {tagline}
        </div>
      </div>

      {/* Language picker */}
      <div className="relative">
        <button
          data-testid="sancta-lang-toggle"
          onClick={() => setLangPickerOpen(!langPickerOpen)}
          aria-label="Change language"
          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-widest bg-white border border-neutral-200 hover:border-orange-400 hover:bg-orange-50 transition-colors"
        >
          <Globe className="h-3 w-3 text-neutral-500" />
          <span className="normal-case tracking-normal text-neutral-700 text-[11px]">{langNativeName}</span>
          {detection && detection.code === langCode && detection.confidence >= 0.7 && (
            <span
              data-testid="sancta-lang-auto-dot"
              title={`Auto-detected from your last message (${detection.source})`}
              className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 ml-0.5"
            />
          )}
        </button>
        {langPickerOpen && (
          <div
            data-testid="sancta-lang-menu"
            className="absolute right-0 mt-1 w-60 max-h-80 overflow-y-auto rounded-xl bg-white shadow-xl shadow-black/10 border border-neutral-200 z-10"
            onMouseLeave={() => setLangPickerOpen(false)}
          >
            {/* Auto-swap toggle */}
            <label
              data-testid="sancta-autoswap-toggle"
              className="flex items-start gap-2 px-3 py-2 border-b border-neutral-100 cursor-pointer hover:bg-neutral-50"
            >
              <input
                type="checkbox"
                checked={autoSwap}
                onChange={e => setAutoSwap(e.target.checked)}
                className="mt-0.5 h-3.5 w-3.5 rounded border-neutral-300 accent-fuchsia-600"
              />
              <span className="flex-1">
                <span className="block text-[12px] font-semibold text-neutral-800 leading-tight">
                  Auto-detect language
                </span>
                <span className="block text-[10px] text-neutral-500 mt-0.5 leading-snug">
                  Sancta will switch to whatever language you type next.
                </span>
              </span>
            </label>
            {/* Auto-read (voice reply) toggle */}
            <label
              data-testid="sancta-autoread-toggle"
              className={`flex items-start gap-2 px-3 py-2 border-b border-neutral-100 cursor-pointer hover:bg-neutral-50 ${ttsSupported ? "" : "opacity-60 cursor-not-allowed"}`}
              title={ttsSupported ? undefined : "Speech synthesis isn't supported in this browser."}
            >
              <input
                type="checkbox"
                checked={autoRead && ttsSupported}
                disabled={!ttsSupported}
                onChange={e => setAutoRead(e.target.checked)}
                className="mt-0.5 h-3.5 w-3.5 rounded border-neutral-300 accent-fuchsia-600"
              />
              <span className="flex-1">
                <span className="flex items-center gap-1 text-[12px] font-semibold text-neutral-800 leading-tight">
                  <Volume2 className="h-3 w-3 text-fuchsia-500" />
                  {autoRead ? voiceReplyOn : voiceReplyOff}
                </span>
                <span className="block text-[10px] text-neutral-500 mt-0.5 leading-snug">
                  {voiceReplyHint}
                </span>
              </span>
            </label>
            {SANCTA_LANGUAGES.map(L => (
              <button
                key={L.code}
                data-testid={`sancta-lang-opt-${L.code}`}
                onClick={() => { setLangCode(L.code); setLangPickerOpen(false); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-neutral-50 flex items-center gap-2 ${L.code === langCode ? "font-semibold text-orange-600" : "text-neutral-700"}`}
              >
                <span className="flex-1 min-w-0">
                  <span className="block text-[13px] leading-tight truncate">{L.nativeName}</span>
                  {L.nativeName !== L.name && (
                    <span className="block text-[10px] text-neutral-400 uppercase tracking-widest">{L.name}</span>
                  )}
                </span>
                {L.code === langCode && <Check className="h-3.5 w-3.5 shrink-0" />}
              </button>
            ))}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onExpand}
        aria-label={`Expand ${chatbotName} to full page`}
        title="Expand to full page"
        data-testid="sancta-expand"
        className={`p-1.5 rounded transition-colors ${variant === "page" ? "hidden" : "hover:bg-neutral-200"}`}
      >
        <Maximize2 className="h-4 w-4 text-neutral-500" />
      </button>

      {showUserActions && (
        <button
          type="button"
          onClick={onMemoryOpen}
          aria-label="What Sancta remembers about you"
          title="Sancta Memory"
          data-testid="sancta-open-memory"
          className={`p-1.5 rounded transition-colors ${variant === "page" ? "hover:bg-neutral-100 dark:hover:bg-white/10" : "hover:bg-neutral-200"}`}
        >
          <BrainCircuit className="h-4 w-4 text-fuchsia-500" />
        </button>
      )}

      <button
        onClick={onClose}
        aria-label="Close"
        className={`p-1.5 rounded transition-colors ${variant === "page" ? "hidden" : "hover:bg-neutral-200"}`}
      >
        <X className="h-4 w-4 text-neutral-500" />
      </button>
    </header>
  );
};

// React.memo is safe here — the component reads ALL its state from
// context. It has zero props, so React.memo's default shallow-equal on
// props is trivially true; a re-render only happens when the CONTEXT
// value's identity changes (guarded by useMemo in SanctaChatProvider).
export const SanctaHeader = React.memo(SanctaHeaderImpl);
SanctaHeader.displayName = "SanctaHeader";

export default SanctaHeader;
